/**
 * useSDD Hook
 *
 * TanStack Query hooks for fetching and managing System Design Documents (SDDs)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SDDDocumentStatus } from '@forgenetwork/types';

/**
 * SDD Response types (matching backend API)
 */
export interface SDDResponse {
  id: string;
  workspaceId: string;
  title: string;
  content: string;
  status: SDDDocumentStatus;
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SDDListResponse {
  sdds: SDDResponse[];
}

export interface GenerateSDDRequest {
  title: string;
  content: string;
  sections?: {
    id: string;
    title: string;
    level: number;
    content: string;
    subsections?: unknown[];
  }[];
}

export interface UpdateSDDRequest {
  title?: string;
  content?: string;
  status?: SDDDocumentStatus;
}

export interface ExportSDDRequest {
  format: 'markdown' | 'json' | 'html';
  includeTableOfContents?: boolean;
  includeMetadata?: boolean;
  prettyPrint?: boolean;
}

/**
 * Fetch a single SDD by ID
 *
 * @param repoId - The repository ID
 * @param sddId - The SDD ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with SDD data
 */
export function useSDD(repoId: string, sddId: string, enabled = true) {
  return useQuery<SDDResponse>({
    queryKey: ['sdd', repoId, sddId],
    queryFn: async () => {
      return await api.get<SDDResponse>(`/repositories/${repoId}/sdd/${sddId}`);
    },
    enabled: enabled && !!repoId && !!sddId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch all SDDs for a repository
 *
 * @param repositoryId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with SDD list
 */
export function useSDDList(repositoryId: string, enabled = true) {
  return useQuery<SDDListResponse>({
    queryKey: ['sdds', repositoryId],
    queryFn: async () => {
      return await api.get<SDDListResponse>(`/repositories/${repositoryId}/sdd`);
    },
    enabled: enabled && !!repositoryId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Mutation to generate a new SDD from repository analysis
 *
 * @returns Mutation object with mutate function
 */
export function useGenerateSDD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      repoId,
      data,
    }: {
      repoId: string;
      data: GenerateSDDRequest;
    }) => {
      return await api.post<SDDResponse>(
        `/repositories/${repoId}/sdd/generate`,
        data
      );
    },
    onSuccess: (_data, variables) => {
      // Invalidate SDD list cache to refetch
      queryClient.invalidateQueries({
        queryKey: ['sdds', variables.repoId],
      });
      // Also invalidate repository cache
      queryClient.invalidateQueries({
        queryKey: ['repository', variables.repoId],
      });
    },
  });
}

/**
 * Mutation to update an existing SDD
 *
 * @returns Mutation object with mutate function
 */
export function useUpdateSDD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      repoId,
      sddId,
      updates,
    }: {
      repoId: string;
      sddId: string;
      updates: UpdateSDDRequest;
    }) => {
      return await api.patch<SDDResponse>(`/repositories/${repoId}/sdd/${sddId}`, updates);
    },
    onSuccess: (_data, variables) => {
      // Invalidate specific SDD cache
      queryClient.invalidateQueries({
        queryKey: ['sdd', variables.repoId, variables.sddId],
      });
      // Invalidate SDD list for this repository
      queryClient.invalidateQueries({
        queryKey: ['sdds', variables.repoId],
      });
    },
  });
}

/**
 * Mutation to update SDD status only
 *
 * @returns Mutation object with mutate function
 */
export function useUpdateSDDStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      repoId,
      sddId,
      status,
    }: {
      repoId: string;
      sddId: string;
      status: SDDDocumentStatus;
    }) => {
      return await api.patch<SDDResponse>(
        `/repositories/${repoId}/sdd/${sddId}/status`,
        { status }
      );
    },
    onSuccess: (_data, variables) => {
      // Invalidate specific SDD cache
      queryClient.invalidateQueries({
        queryKey: ['sdd', variables.repoId, variables.sddId],
      });
      // Invalidate SDD list for this repository
      queryClient.invalidateQueries({
        queryKey: ['sdds', variables.repoId],
      });
    },
  });
}

/**
 * Mutation to delete an SDD
 *
 * @returns Mutation object with mutate function
 */
export function useDeleteSDD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      repoId,
      sddId,
    }: {
      repoId: string;
      sddId: string;
    }) => {
      return await api.delete<void>(`/repositories/${repoId}/sdd/${sddId}`);
    },
    onSuccess: (_data, variables) => {
      // Invalidate SDD list cache
      queryClient.invalidateQueries({
        queryKey: ['sdds', variables.repoId],
      });
      // Remove from specific SDD cache
      queryClient.removeQueries({
        queryKey: ['sdd', variables.repoId, variables.sddId],
      });
    },
  });
}

/**
 * Hook to export SDD in various formats
 *
 * @returns Mutation object with mutate function that triggers download
 */
export function useExportSDD() {
  return useMutation({
    mutationFn: async ({
      repoId,
      sddId,
      options,
    }: {
      repoId: string;
      sddId: string;
      options: ExportSDDRequest;
    }) => {
      const params = new URLSearchParams({
        format: options.format,
        ...(options.includeTableOfContents !== undefined && {
          includeTableOfContents: String(options.includeTableOfContents),
        }),
        ...(options.includeMetadata !== undefined && {
          includeMetadata: String(options.includeMetadata),
        }),
        ...(options.prettyPrint !== undefined && {
          prettyPrint: String(options.prettyPrint),
        }),
      });

      // This triggers a download
      const response = await fetch(
        `/api/repositories/${repoId}/sdd/${sddId}/export?${params}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export SDD');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `sdd.${options.format === 'markdown' ? 'md' : options.format}`;

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { filename };
    },
  });
}

/**
 * Get cached SDD without triggering a fetch
 *
 * @param repoId - The repository ID
 * @param sddId - The SDD ID
 * @returns Cached SDD or undefined
 */
export function useCachedSDD(repoId: string, sddId: string): SDDResponse | undefined {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<SDDResponse>(['sdd', repoId, sddId]);
}

/**
 * Get cached SDD list without triggering a fetch
 *
 * @param repositoryId - The repository ID
 * @returns Cached SDDs or undefined
 */
export function useCachedSDDList(repositoryId: string): SDDResponse[] | undefined {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<SDDListResponse>(['sdds', repositoryId]);
  return cached?.sdds;
}
